import classNames from 'classnames'
import Bullet from '@components/ui/formElements/Bullet'
import SquareRadio from '@components/ui/formElements/Radio'
import Textarea from '@components/ui/formElements/Textarea'
import TextInput from '@components/ui/formElements/TextInput'
import Translations from '@helpers/translations'
import styles from './styles.module.scss'

const components = {
  bullet: Bullet,
  squareRadio: SquareRadio,
  email: TextInput,
  text: TextInput,
  textarea: Textarea
}
const textTypeInputs = {
  text: true,
  textarea: true,
  email: true
}

export type FormContentProps = {
  changeValue: React.ComponentProps<
    (typeof components)[keyof typeof components]
  >['changeValue']
  values: Record<string, string>
  data: {
    answers: string[]
    question: string
    id: string
    type: string
    required: boolean
    hidden: boolean
  }
}

export function FormContent({ changeValue, values, data }: FormContentProps) {
  const {
    answers,
    question,
    id,
    type,
    required,
    hidden: isHidden,
    ...additionalProps
  } = data

  const FormComponent = components[type as keyof typeof components]
  const isTextTypeInput = textTypeInputs[type as keyof typeof textTypeInputs]

  const formRadiosClassNames = classNames(
    {
      [styles.FormRadios]: type === 'bullet' || type === 'squareRadio'
    },
    {
      [styles.FormRadios__inline]: type === 'bullet'
    }
  )

  const formLabelClassName = classNames(styles.FormLabel, {
    [styles.FormLabel__required]: required
  })

  const getBulletLabels = (id: string) => {
    let leftLabel, rightLabel
    switch (id) {
      case 'how_likely_are_you_to_recommend':
        leftLabel = Translations.localise('pages.feedback.answers.least-likely')
        rightLabel = Translations.localise('pages.feedback.answers.most-likely')
        break
      case 'price_to_value':
        leftLabel = Translations.localise('pages.feedback.answers.poor-value')
        rightLabel = Translations.localise(
          'pages.feedback.answers.excellent-value'
        )
        break
      case 'site_navigation':
        leftLabel = Translations.localise(
          'pages.feedback.answers.very-difficult'
        )
        rightLabel = Translations.localise('pages.feedback.answers.very-easy')
        break
      default:
        leftLabel = Translations.localise('pages.feedback.answers.very-poor')
        rightLabel = Translations.localise('pages.feedback.answers.excellent')
    }

    return { leftLabel, rightLabel }
  }

  const { leftLabel, rightLabel } = getBulletLabels(id)

  return (
    <>
      {!id.includes('other') && !isHidden && (
        <p className={formLabelClassName}>{question}</p>
      )}
      <div className={formRadiosClassNames}>
        {answers.map((label, index) => (
          <FormComponent
            id={id}
            key={`${id}_${label}`}
            isSelected={values[id] === label}
            changeValue={changeValue}
            label={label}
            value={isTextTypeInput ? values[id] : label}
            index={index}
            placeholder={
              type === 'email'
                ? Translations.localise('common.form.enter-email-address')
                : ''
            }
            required={required}
            type={type}
            hidden={isHidden}
            {...additionalProps}
          />
        ))}
      </div>
      {type === 'bullet' && (
        <div className={styles.FormDetails}>
          <p className={styles.FormDetails__one}>{leftLabel}</p>
          <p className={styles.FormDetails__two}>1 - 10</p>
          <p className={styles.FormDetails__three}>{rightLabel}</p>
        </div>
      )}
    </>
  )
}
